import api from './api';
import {
  InfoEntrepriseDto,
  InfoEntrepriseCreateDto,
  InfoEntrepriseUpdateDto,
  SuccursaleDto,
  SuccursaleCreateDto,
  SuccursaleUpdateDto,
  PaginatedResponse,
} from '../types/django';

const toFormData = (data: InfoEntrepriseCreateDto | InfoEntrepriseUpdateDto): FormData => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      formData.append(key, '');
    } else if (value instanceof File) {
      formData.append(key, value);
    } else if (typeof value === 'string' && value.startsWith('blob:')) {
      return;
    } else {
      formData.append(key, String(value));
    }
  });

  return formData;
};

export const entrepriseService = {
  async getAll(): Promise<InfoEntrepriseDto[]> {
    const response = await api.get('/entreprises/');
    return response.data.results ?? response.data;
  },

  async getPaginated(params: {
    page?: number;
    page_size?: number;
    search?: string;
    ordering?: string;
  } = {}): Promise<PaginatedResponse<InfoEntrepriseDto> | InfoEntrepriseDto[]> {
    const response = await api.get('/entreprises/', { params });
    return response.data;
  },

 async getById(id?: number): Promise<InfoEntrepriseDto> {
    if (id) {
      const response = await api.get(`/entreprises/${id}/`);
      return response.data;
    }

    const response = await api.get('/entreprises/');
    const data = response.data;

    if (data && typeof data === 'object' && 'results' in data) {
      return data.results[0];
    }

    return Array.isArray(data) ? data[0] : data;
  },

  async create(entreprise: InfoEntrepriseCreateDto): Promise<InfoEntrepriseDto> {
    const formData = toFormData(entreprise);
    const response = await api.post('/entreprises/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async update(id: number, entreprise: InfoEntrepriseUpdateDto): Promise<InfoEntrepriseDto> {
    const formData = toFormData(entreprise);
    const response = await api.patch(`/entreprises/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/entreprises/${id}/`);
  },

  async getSuccursales(entrepriseId: number): Promise<SuccursaleDto[]> {
    const response = await api.get('/succursales/', {
      params: { entreprise: entrepriseId },
    });
    return response.data.results ?? response.data;
  },
};

export const succursaleService = {
  async getAll(entrepriseId?: number): Promise<SuccursaleDto[]> {
    const params = entrepriseId ? { entreprise: entrepriseId } : {};
    const response = await api.get('/succursales/', { params });
    return response.data.results ?? response.data;
  },

  async getPaginated(params: {
    page?: number;
    page_size?: number;
    search?: string;
    ordering?: string;
    entreprise?: number;
  } = {}): Promise<PaginatedResponse<SuccursaleDto> | SuccursaleDto[]> {
    const response = await api.get('/succursales/', { params });
    return response.data;
  },

  async getById(id: number): Promise<SuccursaleDto> {
    const response = await api.get(`/succursales/${id}/`);
    return response.data;
  },

  async create(succursale: SuccursaleCreateDto): Promise<SuccursaleDto> {
    const response = await api.post('/succursales/', succursale);
    return response.data;
  },

  async update(id: number, succursale: SuccursaleUpdateDto): Promise<SuccursaleDto> {
    const response = await api.patch(`/succursales/${id}/`, succursale);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/succursales/${id}/`);
  },
};